// channelStore.ts — live tool-UI channel state, held OUTSIDE React.
//
// This module is the reason a terminal keeps its scrollback when a tool
// replaces its UI. React's reconciler will usually preserve a keyed
// component across a re-render, but "usually" is not a guarantee: a
// StrictMode double-mount, a parent that changes shape, or a key collision
// all remount, and a socket plus buffer owned by component state would go
// with it.
//
// So the socket, the accumulated frames, and the connection status live in
// a module-level map keyed by component id. Components subscribe with
// useSyncExternalStore and are free to unmount and remount at will — the
// new instance re-reads the same buffer and the same open socket. Remount
// becomes cheap and lossless rather than destructive.
//
// Buffers are per-component-kind because the shapes differ: a terminal
// accumulates lines, a linegraph accumulates points, a progressbar keeps
// only the latest value. Each is bounded.

export type ChannelStatus = 'connecting' | 'open' | 'closed' | 'error';

/** Ring-buffer ceilings. Past these, the oldest data is dropped. */
export const MAX_TERMINAL_LINES = 5000;
export const MAX_GRAPH_POINTS = 2000;

/** Reconnect backoff, in ms. Capped so a dead Pod isn't hammered. */
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15000;

export interface GraphPoint {
    x: number;
    y: number;
}

export interface ChannelState {
    status: ChannelStatus;
    /** Highest sequence number seen, replayed from on reconnect. */
    lastSeq: number;
    /** terminal */
    lines: string[];
    /** progressbar */
    progress: { value?: number; max?: number; label?: string; indeterminate: boolean };
    /** linegraph */
    points: GraphPoint[];
    /** controlpanel */
    buttons: Array<{ id: string; label: string; variant?: string; disabled?: boolean }>;
    /** Set when the server reports a problem on this channel. */
    error: string;
    /**
     * The panel has been answered and is now inert.
     *
     * Set the instant a button is pressed, not when the server replies.
     * An approval round-trip goes browser → Pod → MCP server and back; a
     * second click inside that window would send a second decision, and
     * for a gate that unblocks a waiting call the first Allow has already
     * done the thing. Latching locally is what makes double-clicking Allow
     * a no-op rather than two approvals.
     */
    answered: boolean;
    /**
     * A click taken before the socket was ready, still waiting to go out.
     *
     * The panel's buttons are seeded into the markup and paint immediately,
     * but the socket needs a handshake — so there was a window in which the
     * prompt looked ready and the press did nothing. Holding the click and
     * flushing it on open closes that window, and it does so whatever the
     * delay was: a slow handshake and a first attempt that failed into the
     * reconnect backoff both look the same from here.
     */
    sending: boolean;
}

const EMPTY: ChannelState = {
    status: 'connecting',
    lastSeq: 0,
    lines: [],
    progress: { indeterminate: true },
    points: [],
    buttons: [],
    error: '',
    answered: false,
    sending: false,
};

interface Entry {
    state: ChannelState;
    socket: WebSocket | null;
    listeners: Set<() => void>;
    /** Components currently mounted against this id. */
    refs: number;
    url: string;
    // Bearer token for the handshake. Supplied by the caller rather than
    // read from apiClient here: this module is pure transport + state, and
    // keeping app imports out of it is what lets it be unit-tested in a
    // plain node environment with a fake WebSocket.
    token: string;
    attempt: number;
    reconnectTimer: number | null;
    closedByUs: boolean;
    /** Click taken while the socket was down; flushed on open. */
    pending: unknown | null;
}

const entries = new Map<string, Entry>();

/** Replaces the whole tool UI. Set once by Sessions.tsx. */
let uiSink: ((toolCallId: string, markdown: string) => void) | null = null;
export function setUiSink(fn: (toolCallId: string, markdown: string) => void): void {
    uiSink = fn;
}

/** Which tool call owns each component id, for routing `ui` frames. */
const owners = new Map<string, string>();

function entryFor(key: string): Entry {
    let entry = entries.get(key);
    if (!entry) {
        entry = {
            state: { ...EMPTY, lines: [], points: [], buttons: [] },
            socket: null,
            listeners: new Set(),
            refs: 0,
            url: '',
            token: '',
            attempt: 0,
            reconnectTimer: null,
            closedByUs: false,
            pending: null,
        };
        entries.set(key, entry);
    }
    return entry;
}

function emit(entry: Entry): void {
    for (const listener of entry.listeners) listener();
}

function patch(entry: Entry, next: Partial<ChannelState>): void {
    // A new object identity every time: useSyncExternalStore compares by
    // reference, so mutating in place would render nothing.
    entry.state = { ...entry.state, ...next };
    emit(entry);
}

/**
 * Connect (or reuse) the socket backing one component.
 *
 * Idempotent per key: a remount increments the ref count and finds the
 * socket already open, which is the whole point of this module.
 */
export function acquire(
    key: string, url: string, toolCallId: string, token = '',
): void {
    const entry = entryFor(key);
    entry.refs += 1;
    if (toolCallId) owners.set(key, toolCallId);
    if (token) entry.token = token;
    if (entry.socket || !url) {
        entry.url = url || entry.url;
        return;
    }
    entry.url = url;
    open(key);
}

/**
 * Drop one component's reference.
 *
 * The socket is NOT closed here. A UI replacement unmounts and immediately
 * remounts, and tearing the connection down in between would lose the
 * buffer and re-replay the whole stream. `release` is a bookkeeping call;
 * `evict` is what actually disconnects.
 */
export function release(key: string): void {
    const entry = entries.get(key);
    if (!entry) return;
    entry.refs = Math.max(0, entry.refs - 1);
}

/** Close and forget a channel — called when its component is truly gone. */
export function evict(key: string): void {
    const entry = entries.get(key);
    if (!entry) return;
    entry.closedByUs = true;
    if (entry.reconnectTimer !== null) window.clearTimeout(entry.reconnectTimer);
    entry.socket?.close();
    entries.delete(key);
    owners.delete(key);
}

/** Close every channel — called when the chat pane unmounts. */
export function evictAll(): void {
    for (const key of [...entries.keys()]) evict(key);
}

/**
 * Drop channels no longer referenced by any rendered UI.
 *
 * Called after a UI replacement settles, so a component that survived the
 * swap keeps its socket while one that genuinely went away is cleaned up.
 */
export function evictUnreferenced(liveKeys: Set<string>): void {
    for (const [key, entry] of entries) {
        if (!liveKeys.has(key) && entry.refs === 0) evict(key);
    }
}

function open(key: string): void {
    const entry = entries.get(key);
    if (!entry || !entry.url) return;

    // Resume from where we left off rather than replaying the whole
    // stream: the harness buffers by sequence number for exactly this.
    const url = new URL(entry.url, window.location.origin);
    if (entry.state.lastSeq > 0) url.searchParams.set('last_seq', String(entry.state.lastSeq));
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

    // A browser cannot set an Authorization header on a WebSocket, and this
    // SPA authenticates primarily by Bearer — cookies are only the
    // fallback. So the token rides the subprotocol, which the Pod unpacks
    // (see _bearer_subprotocol in app/main.py). Deliberately not a query
    // parameter: those end up in proxy logs.
    let socket: WebSocket;
    try {
        socket = entry.token
            ? new WebSocket(url.toString(), ['bearer', entry.token])
            : new WebSocket(url.toString());
    } catch {
        patch(entry, { status: 'error', error: 'could not open channel' });
        return;
    }
    entry.socket = socket;
    entry.closedByUs = false;
    patch(entry, { status: 'connecting' });

    socket.onopen = () => {
        entry.attempt = 0;
        patch(entry, { status: 'open', error: '' });
        flushPending(key);
    };
    socket.onmessage = (event) => {
        try {
            const frame = JSON.parse(String(event.data));
            if (frame?.keepalive) return;
            applyFrame(key, frame);
        } catch {
            // A frame we can't parse is one frame lost, not a dead socket.
        }
    };
    socket.onerror = () => {
        patch(entry, { error: 'channel error' });
    };
    socket.onclose = () => {
        entry.socket = null;
        if (entry.closedByUs) return;
        patch(entry, { status: 'closed' });
        scheduleReconnect(key);
    };
}

function scheduleReconnect(key: string): void {
    const entry = entries.get(key);
    if (!entry || entry.closedByUs) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** entry.attempt, RECONNECT_MAX_MS);
    entry.attempt += 1;
    entry.reconnectTimer = window.setTimeout(() => {
        entry.reconnectTimer = null;
        if (entries.has(key)) open(key);
    }, delay);
}

function applyFrame(key: string, frame: { seq?: number; data?: unknown }): void {
    const entry = entries.get(key);
    if (!entry) return;
    const data = frame.data as Record<string, unknown> | undefined;
    if (typeof frame.seq === 'number') entry.state = { ...entry.state, lastSeq: frame.seq };
    if (!data || typeof data !== 'object') return;

    // A `ui` frame replaces the whole tool UI rather than updating one
    // component — the only update path once a tool result has returned.
    if (typeof data.ui === 'string') {
        const owner = owners.get(key);
        if (owner && uiSink) uiSink(owner, data.ui);
        return;
    }

    if (typeof data.error === 'string') {
        patch(entry, { error: data.error });
        return;
    }

    // terminal
    if (data.clear === true) {
        patch(entry, { lines: [] });
        return;
    }
    if (typeof data.append === 'string') {
        const incoming = data.append.split('\n');
        const lines = [...entry.state.lines, ...incoming];
        patch(entry, {
            lines: lines.length > MAX_TERMINAL_LINES ? lines.slice(-MAX_TERMINAL_LINES) : lines,
        });
        return;
    }

    // linegraph
    if (data.reset === true) {
        patch(entry, { points: [] });
        return;
    }
    if (Array.isArray(data.points)) {
        const incoming: GraphPoint[] = [];
        for (const p of data.points) {
            if (Array.isArray(p) && p.length >= 2) {
                const x = Number(p[0]);
                const y = Number(p[1]);
                if (Number.isFinite(x) && Number.isFinite(y)) incoming.push({ x, y });
            }
        }
        const points = [...entry.state.points, ...incoming];
        patch(entry, {
            points: points.length > MAX_GRAPH_POINTS ? points.slice(-MAX_GRAPH_POINTS) : points,
        });
        return;
    }

    // controlpanel
    if (Array.isArray(data.buttons)) {
        patch(entry, {
            buttons: data.buttons
                .filter((b: unknown): b is Record<string, unknown> => !!b && typeof b === 'object')
                .map((b) => ({
                    id: String(b.id ?? ''),
                    label: String(b.label ?? b.id ?? ''),
                    variant: typeof b.variant === 'string' ? b.variant : undefined,
                    disabled: b.disabled === true,
                }))
                .filter((b) => b.id),
        });
        return;
    }

    // progressbar
    if ('value' in data || 'max' in data || data.indeterminate === true) {
        const value = Number(data.value);
        const max = Number(data.max);
        patch(entry, {
            progress: {
                value: Number.isFinite(value) ? value : undefined,
                max: Number.isFinite(max) ? max : entry.state.progress.max,
                label: typeof data.label === 'string' ? data.label : entry.state.progress.label,
                indeterminate: data.indeterminate === true || !Number.isFinite(value),
            },
        });
    }
}

/**
 * Send a frame up to the MCP server that owns this channel.
 *
 * A click also latches the panel closed. Returns false when the socket
 * isn't open OR the panel has already been answered, so the caller can
 * tell "didn't send" from "sent" without having to track that itself.
 */
export function send(key: string, payload: unknown): boolean {
    const entry = entries.get(key);
    if (!entry) return false;
    if (entry.state.answered || entry.state.sending) return false;

    if (!entry.socket || entry.socket.readyState !== WebSocket.OPEN) {
        // Hold it rather than drop it. A press is the user's decision and
        // it does not stop being their decision because a handshake is
        // still in flight — the alternative was a live-looking button that
        // silently ate the click, which is why it used to be disabled
        // until the socket opened.
        entry.pending = payload;
        patch(entry, { sending: true });
        return true;
    }
    transmit(entry, payload);
    return true;
}

/** Write a payload out and latch `answered` if it was a click. */
function transmit(entry: Entry, payload: unknown): void {
    entry.socket?.send(JSON.stringify(payload));
    if (payload && typeof payload === 'object' && 'click' in payload) {
        patch(entry, { answered: true, sending: false });
    } else {
        patch(entry, { sending: false });
    }
}

/** Send whatever was clicked while the socket was down. */
function flushPending(key: string): void {
    const entry = entries.get(key);
    if (!entry || entry.pending === null) return;
    const payload = entry.pending;
    entry.pending = null;
    transmit(entry, payload);
}

// ── useSyncExternalStore plumbing ───────────────────────────────────

export function subscribe(key: string, listener: () => void): () => void {
    const entry = entryFor(key);
    entry.listeners.add(listener);
    return () => { entry.listeners.delete(listener); };
}

export function snapshot(key: string): ChannelState {
    return entries.get(key)?.state ?? EMPTY;
}
